import {
  Button,
  Fab,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import useQueryUrl from 'src/hooks/useQuery';
import { OFFLINE_MEDIA_CLIPPING } from 'src/route';
import useOfflineMedia from 'src/services/useOfflineMedia';
import { downloadCsv } from 'src/utils/downloadCsv';
import { downloadXlsx } from 'src/utils/downloadXlsx';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TExportType } from 'src/models/download';
import useDownload from 'src/services/useDownload';
import { useAlert } from 'src/hooks/useAlert';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { Packer } from 'docx';
import { saveAs } from 'file-saver';
import { generateWhatsappReportDoc } from 'src/utils/generateWhatsappReportDoc';
import { generateArticleBySentimentDoc } from 'src/utils/generateArticleBySentimentDoc';

const TopHeaderOfmClippingById = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const query = useQueryUrl();
  const id = pathname.split('/')[3];
  const navigate = useNavigate();
  const offlineMediaType = query.get('type');
  const portal = query.get('portal');
  const articleId = query.get('id');
  const { handleClickAlert } = useAlert();

  const { articleList, languageSelected, streamSelected, activeArticle } =
    useAppSelector((state) => state.storeOfflineMediaStream);

  const advSearchOfflineMedia = useAppSelector(
    (state) => state.advanceSearchOfflineMedia
  );

  const { labelObjectList } = advSearchOfflineMedia;

  const fillAdvanceSearchOfflineMedia = fillParamAdvanceSearchEmpty(
    advSearchOfflineMedia
  );

  const timezone = useAppSelector((state) => state.users.project.timezone);
  const projectId = useAppSelector((state) => state.users.project.id);
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );

  const { getClippingOfflineMediaById, resGetClippingOfflineMediaById } =
    useOfflineMedia();
  const { downloadCsvMediaOffline } = useDownload();

  useEffect(() => {
    getClippingOfflineMediaById({
      variables: {
        clippingId: id
      }
    });
  }, [id]);

  const [anchorElDownload, setAnchorElDownload] =
    useState<HTMLButtonElement | null>(null);

  const handleClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
  };

  const handleDownload = (exportType: TExportType = 'csv') => {
    handleCloseDownload();

    const finalPayload = {
      ...fillAdvanceSearchOfflineMedia,
      ...dateRangeFormatted,
      clippingId: id,
      includeKeywords: advSearchOfflineMedia.includeKeywords,
      languageList:
        languageSelected === 'all-language' ? [] : [languageSelected],
      projectId,
      granularity: {
        unit: 'day',
        value: 1
      },
      timezone
    };

    downloadCsvMediaOffline({
      variables: {
        ...finalPayload,
        mediaList: !portal || portal === 'all-media' ? [] : [portal],
        exportType: exportType,
        offlineMediaType: offlineMediaType
      },
      onCompleted: () =>
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          severity: 'success',
          message:
            'It may take some time for the report to be processed.\n The report can be downloaded from the Activity List on the top right corner.'
        }),
      onError(error) {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          severity: 'error',
          message: 'Failed to download report, please try again.'
        });
        console.log(error.message);
      }
    });
  };

  const handleDownloadBySentiment = () => {
    const [selectedLabel, ...rest] = labelObjectList;
    const clippingName =
      resGetClippingOfflineMediaById.data?.article_getClippingArticleById?.name;
    const doc = generateArticleBySentimentDoc(
      articleList.result,
      selectedLabel?.displayName ?? clippingName
    );
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `OFM - ${selectedLabel?.displayName ?? clippingName}.docx`);
    });
    handleCloseDownload();
  };

  const handleDownloadWhatsappReport = () => {
    if (articleId) {
      const doc = generateWhatsappReportDoc([activeArticle]);
      Packer.toBlob(doc).then((blob) => {
        saveAs(blob, `OFM - ${activeArticle.originalId}.docx`);
      });
    } else {
      // download all articles
      const doc = generateWhatsappReportDoc(articleList.result);
      Packer.toBlob(doc).then((blob) => {
        saveAs(
          blob,
          `OFM - ${resGetClippingOfflineMediaById.data.article_getClippingArticleById.name}.docx`
        );
      });
    }
  };

  const openDownload = Boolean(anchorElDownload);
  const idPopup = openDownload ? 'simple-popover' : undefined;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={2}
      alignItems="center"
      sx={{ mb: 2, background: 'transparent' }}
    >
      <Stack direction="row" alignItems="center">
        <Fab
          sx={{
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[200]}`,
            mr: theme.spacing(2),
            boxShadow: 'none',
            '&:hover': { background: '#f5f5f5' }
          }}
          size="small"
          onClick={() =>
            navigate(
              `${OFFLINE_MEDIA_CLIPPING}${
                offlineMediaType ? `?type=${offlineMediaType}` : ''
              }`
            )
          }
        >
          <ArrowBackIcon />
        </Fab>
        {!resGetClippingOfflineMediaById.loading && (
          <Typography variant="h1">
            {
              resGetClippingOfflineMediaById.data
                ?.article_getClippingArticleById.name
            }
          </Typography>
        )}
      </Stack>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          height: 40,
          borderRadius: '32px',
          bgcolor: 'white',
          color: theme.palette.primary.main,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          boxShadow: 'none',
          border: `1px solid ${theme.palette.primary.main}`,
          '&:hover': {
            bgcolor: 'rgba(0, 159, 227, 0.04)',
            borderColor: theme.palette.primary.dark,
            border: `1px solid ${theme.palette.primary.dark}`,
            boxShadow: 'none'
          },
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            color: theme.palette.primary.main
          }
        }}
        onClick={handleClickDownload}
      >
        Download Report
      </Button>
      <Popover
        id={idPopup}
        open={openDownload}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownload}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{ mt: 1 }}
      >
        <List sx={{ minWidth: 200 }}>
          <ListItemButton onClick={() => handleDownload()}>
            <ListItemText primary="CSV - All Metrics" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownload('xlsx')}>
            <ListItemText primary="XLSX - All Metrics" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadWhatsappReport()}>
            <ListItemText primary="Word - Whatsapp Report" />
          </ListItemButton>
          <ListItemButton onClick={() => handleDownloadBySentiment()}>
            <ListItemText primary="Word - Articles by Label" />
          </ListItemButton>
          {streamSelected && streamSelected.length > 0 && (
            <>
              <ListItemButton
                onClick={() =>
                  downloadCsv(
                    'custom',
                    streamSelected,
                    `selected-stream-${
                      resGetClippingOfflineMediaById.data
                        ?.article_getClippingArticleById.name || ''
                    }`
                  )
                }
              >
                <ListItemText primary="CSV - Selected Stream" />
              </ListItemButton>
              <ListItemButton
                onClick={() =>
                  downloadXlsx(
                    'custom',
                    streamSelected,
                    `selected-stream-${
                      resGetClippingOfflineMediaById.data
                        ?.article_getClippingArticleById.name || ''
                    }`,
                    'Articles'
                  )
                }
              >
                <ListItemText primary="XLSX - Selected Stream" />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>
    </Stack>
  );
};

export default TopHeaderOfmClippingById;
